import { LETTER_TEMPLATE_PREVIEW_SAMPLES } from '../adminProspectLetterTemplateDisplay.js';
import {
  LETTER_BODY_FONT_SIZE_PT,
  LETTER_PRINTABLE_HEIGHT_PT,
  LETTER_PRINTABLE_WIDTH_PT,
} from './letterTemplatePageConstants.js';
import {
  LETTER_TEMPLATE_DEFAULT_FONT_SIZE_PT,
  LETTER_TEMPLATE_HEADING_FONT_SIZES_PT,
} from './LetterFontSizeExtension.js';

function resolvePlaceholderKey(key) {
  if (Object.prototype.hasOwnProperty.call(LETTER_TEMPLATE_PREVIEW_SAMPLES, key)) {
    return LETTER_TEMPLATE_PREVIEW_SAMPLES[key];
  }
  return '';
}

/**
 * @param {Array<{ type?: string, attrs?: { pt?: number } }> | undefined} marks
 * @param {{ blockType?: string, headingLevel?: number }} [context]
 */
export function resolveLetterTemplatePreviewFontSizePt(marks, { blockType, headingLevel } = {}) {
  const sizeMark = Array.isArray(marks)
    ? marks.find((mark) => mark?.type === 'letterFontSize')
    : null;
  if (sizeMark?.attrs?.pt != null) {
    return Number(sizeMark.attrs.pt);
  }
  if (blockType === 'heading') {
    return LETTER_TEMPLATE_HEADING_FONT_SIZES_PT[headingLevel] || LETTER_BODY_FONT_SIZE_PT;
  }
  return LETTER_BODY_FONT_SIZE_PT;
}

/**
 * @param {object | null | undefined} document
 */
export function extractLetterTemplateQrBlock(document) {
  if (!document || document.type !== 'doc' || !Array.isArray(document.content)) return null;
  return document.content.find((node) => node?.type === 'letterQrBlock') || null;
}

/**
 * @param {object | null | undefined} document
 */
export function filterQrBlocksFromLetterTemplateDocument(document) {
  if (!document || document.type !== 'doc' || !Array.isArray(document.content)) return document;
  return {
    ...document,
    content: document.content.filter((node) => node?.type !== 'letterQrBlock'),
  };
}

/**
 * @param {object | null | undefined} frenchDocument
 * @param {object | null | undefined} englishDocument
 * @param {boolean} bilingual
 */
export function resolveBilingualSharedQrPreview(frenchDocument, englishDocument, bilingual) {
  if (!bilingual) {
    return { sharedQr: null, omitFromFlow: false };
  }
  const sharedQr =
    extractLetterTemplateQrBlock(frenchDocument) || extractLetterTemplateQrBlock(englishDocument);
  return {
    sharedQr,
    omitFromFlow: Boolean(sharedQr),
  };
}

/**
 * Logical overflow before visual scale transform.
 * Compare natural content height in logical CSS px against the printable viewport (684).
 * @param {number} naturalContentHeightPx
 * @param {number} [printableHeightPx]
 */
export function measureLetterTemplateLogicalOverflow(
  naturalContentHeightPx,
  printableHeightPx = LETTER_PRINTABLE_HEIGHT_PT,
) {
  if (!Number.isFinite(naturalContentHeightPx) || naturalContentHeightPx <= 0) return false;
  return naturalContentHeightPx > printableHeightPx + 1;
}

/**
 * @param {{ scrollHeight: number }} contentEl
 * @param {{ clientHeight: number }} viewportEl
 */
export function measureLetterTemplatePreviewOverflow(contentEl, viewportEl) {
  if (!contentEl || !viewportEl) return false;
  return contentEl.scrollHeight > viewportEl.clientHeight + 2;
}

export function collectLetterTemplatePreviewText(document, t) {
  const parts = [];
  const walkInline = (content) => {
    if (!Array.isArray(content)) return;
    for (const node of content) {
      if (node.type === 'text') parts.push(node.text || '');
      if (node.type === 'letterPlaceholder') {
        parts.push(resolvePlaceholderKey(node.attrs?.key));
      }
    }
  };
  const walk = (nodes) => {
    if (!Array.isArray(nodes)) return;
    for (const node of nodes) {
      if (node.type === 'paragraph' || node.type === 'heading') walkInline(node.content);
      if (node.type === 'letterQrBlock') parts.push(t('admin.prospection.letterTemplate.preview.qrMock'));
      if (Array.isArray(node.content)) walk(node.content);
    }
  };
  if (document?.type === 'doc') walk(document.content);
  return parts.join(' ');
}

export function letterTemplatePreviewPreservesBoldMark(document) {
  const paragraph = document?.content?.find((n) =>
    n.content?.some((c) => c.type === 'letterPlaceholder' && c.attrs?.key === 'PROPERTY_ADDRESS'),
  );
  const placeholder = paragraph?.content?.find((c) => c.type === 'letterPlaceholder');
  return placeholder?.marks?.some((m) => m.type === 'bold') ?? false;
}

export function letterTemplatePreviewContainsTokenConcept(document) {
  const blob = JSON.stringify(document || {});
  return /publicAccess|tokenHash|token/i.test(blob);
}

export {
  LETTER_PRINTABLE_HEIGHT_PT,
  LETTER_PRINTABLE_WIDTH_PT,
};
